import type { DrawColor } from '../VideoCanvasOverlay';

/**
 * The editor's control values, shared with the live room's match player:
 * the tools, the speeds, the pen colours and how a speed sets a video.
 * Lifted out of ClipEditorModal unchanged (editorControls.tsx holds the
 * components that use them).
 */

/**
 * The tools a drawing toolbar can offer. The editor uses brush and eraser;
 * the live room's match player adds arrow and circle (opt in through
 * DrawToolbar's `tools`). 'none' is no tool, the video takes the pointer.
 */
export type DrawingTool = 'brush' | 'eraser' | 'arrow' | 'circle' | 'none';
/** The editor's own tools, as it always had them. */
export type EditorTool = Exclude<DrawingTool, 'arrow' | 'circle'>;
export const SPEEDS = [1, 0.5, 0.25] as const;
export type Speed = (typeof SPEEDS)[number];

/**
 * Every colour the pen can paint. Complete, and unchanged.
 *
 * This is the render map, not the picker, and the two are deliberately
 * separate. Nothing persisted reaches this map — a stroke drawn here is baked
 * into pixels, and the only index is local `drawColor` state. Persisted
 * strokes carry a colour *name* (`color: DrawColor`) and are resolved by
 * VideoCanvasOverlay against DRAW_COLOR_HEX in drawColors.ts, which still
 * holds all six and is untouched.
 *
 * It stays total anyway. `Record<DrawColor, string>` is a promise the type
 * system checks, and narrowing it to whatever the picker currently offers
 * would silently make the map partial the next time something does index it
 * with a stored value.
 */
export const COLOR_HEX: Record<DrawColor, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  white: '#ffffff',
  black: '#111827',
};

/**
 * What a coach can choose. Four, in order of how well they hold up on video.
 *
 * Black is gone because #111827 is all but invisible both on this toolbar and
 * in the shadowed half of any outdoor frame. Blue is gone because #3b82f6 is
 * the closest of the six to a hard court and to the editor's own accent, so a
 * blue stroke reads as chrome rather than as coaching. Neither removal touches
 * COLOR_HEX, so a draft that used either still renders exactly as before.
 */
export const DRAW_COLORS: { value: DrawColor; hex: string; label: string }[] = [
  { value: 'red', hex: COLOR_HEX.red, label: 'Red' },
  { value: 'yellow', hex: COLOR_HEX.yellow, label: 'Yellow' },
  { value: 'green', hex: COLOR_HEX.green, label: 'Green' },
  { value: 'white', hex: COLOR_HEX.white, label: 'White' },
];

export function applyVideoState(video: HTMLVideoElement, speed: Speed, muted: boolean) {
  video.muted = muted || speed < 1;
  video.playbackRate = speed;
}

